<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { groupModule } from '@/store/group/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import { userModule } from '@/store/user/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import FilterDropdown from '@/components/Filters/FilterDropdown.vue'
import type { ComponentPublicInstance } from 'vue'
import type { Study } from '@pocketprep/types'
import type { TChoiceKey } from '@pocketprep/types/Study/Cloud'

type TName = {
    id: string
    name: string
    isFlagged: boolean
}

type TGroupFlaggedQuestions = {
    prompt: string
    subject: string
    numStudentsFlagged: number
    serial: string
    correctAnswerCount: number
    totalAnswers: number
    isMockQuestion: boolean
    answers: {
        isCorrect: boolean
        selectedChoices: Study.Cloud.TChoiceKey[]
    }[]
    scenarioPartLabel?: string
}

const {
    QuizContainer,
    Button: PocketButton,
    KeyboardShortcutsButton,
    Icon,
    Tooltip,
    KeyboardShortcutsModal,
    Question,
    QuestionReviewPagination,
    GlobalMetricsToggle,
    Checkbox,
    OverflowTooltip,
} = UIKit

const route = useRoute()
const router = useRouter()

const brandColors = BrandColors
const quizContainer = ref<ComponentPublicInstance | null>(null)
const filterType = ref('all')
const showSideBar = ref(true)
const listViewHover = ref(false)
const fullViewHover = ref(false)
const showListViewTooltip = ref(false)
const showFullViewTooltip = ref(false)
const isLoading = ref(true)
const filterSubjects = ref<string[]>([])
const showFilterDropdown = ref(false)
const checkboxSubjects = ref<string[]>([])
const showNamesChecked = ref(false)
const showGroupMetrics = ref(true)

const groupStats = computed(() => {
    return statsModule.getters.getCurrentGroupAggStats()
})

const allExamQuestions = computed(() => {
    return examModule.getters.getCurrentExamQuestionsNoMock()
})

const currentExamName = computed(() => {
    return examModule.getters.getCurrentGroupExam()?.displayName
})

const currentExamMetadata = computed(() => {
    return examModule.getters.getCurrentExamMetadata()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const currentGroup = computed(() => {
    return groupModule.getters.getCurrentGroup()?.name
})

const groupFlagged = computed(() => {
    return groupStats.value.questionStats.filter(q => !q.isMockQuestion && q.numStudentsFlagged > 0)
})

const emit = defineEmits<{
    close: []
    toggleShowNames: [showNames: boolean]
}>()

const changePage = (newPage: number) => {
    if (newPage > questionIndex.value) {
        // Paging FORWARD
        clickNext()
    } else if (newPage < questionIndex.value) {
        // Paging BACKWARD
        clickPrevious()
    }
}

const questionScenarioPartLabel = (question: Study.Class.QuestionJSON) => {
    return examModule.getters.getScenarioPartLabel(question)
}

const formattedReplacedPrompt = (prompt: string) => {
    return prompt.replace(/<p>/g, '')
        .replace(/<\/p>/g, '')
        .replace(/<strong>/g, '')
        .replace(/<\/strong>/g, '')
}

const formattedQuestionPrompt = (question: Study.Class.QuestionJSON) => {
    const scenarioPartLabel = questionScenarioPartLabel(question)
    const formattedPrompt = formattedReplacedPrompt(question.prompt)
    return scenarioPartLabel ? `${scenarioPartLabel}: ${formattedPrompt}` : formattedPrompt
}

const formattedGroupFlaggedQuestionPrompt = (question: TGroupFlaggedQuestions) => {
    const formattedPrompt = formattedReplacedPrompt(question.prompt)
    return question.scenarioPartLabel ? `${question.scenarioPartLabel}: ${formattedPrompt}` : formattedPrompt
}

const currentGroupName = computed(() => {
    return groupModule.getters.getCurrentGroup()?.name
})

const groupFlaggedQuestions = computed(() => {
    if (filterSubjects.value.length && groupFlagged.value.length) {
        return groupFlagged.value.filter(q => {
            return filterSubjects.value.includes(q.subject)
        })
    }
    return groupFlagged.value
})

const allQuestions = computed(() => {
    if (filterSubjects.value.length && allExamQuestions.value.length) {
        const questions = allExamQuestions.value.filter(q => {
            const s = q.subject as Study.Class.SubjectJSON
            return filterSubjects.value.includes(s.name)
        })

        return questions
    }
    return allExamQuestions.value
})

const studentAggStats = computed(() => {
    return statsModule.getters.getStudentAggStats()
})

const students = computed(() => {
    return studentModule.getters.getCurrentGroupStudents({ excludePendingStudents: true })
})

const currentOrgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const currentSerial = computed(() => {
    return route.query.questionSerial as string
})

const isFlaggedQuestion = computed(() => {
    return (route.query.type as string) === 'flagged'
})

const studentsAnsweredByAnswerChoice = computed(() => {
    const studentAggStatsVal = studentAggStats.value
    const currentSerialVal = currentSerial.value
    return Object.values(students.value).reduce((acc, student) => {
        const stats = studentAggStatsVal[student.objectId]
        const studentUser = student.user && currentOrgStudentUsers.value[student.user.objectId]

        // Did the student answer this question?
        const answeredQuestionChoices = stats?.questionAnswerLib[currentSerialVal]

        // Did the student flag this question?
        const isFlaggedByStudent = !!stats?.flaggedQuestionSerials.find(qs => qs === currentSerialVal)

        // We want first name last initial
        const name = student.displayFirstName || student.displayLastName
            ? [ student.displayFirstName, student.displayLastName?.charAt(0).toUpperCase() ].join(' ')
            : studentUser
                ? studentUser.firstName || studentUser.lastName
                    ? [ studentUser.firstName, studentUser.lastName?.charAt(0).toUpperCase() ].join(' ')
                    : studentUser.username
                : ''

        // If they answered the question, extract their choices and assign to acc
        if (answeredQuestionChoices && answeredQuestionChoices.length > 0) {
            answeredQuestionChoices.forEach(sc => {
                if (!acc[sc]) {
                    acc[sc] = []
                }
                acc[sc].push({
                    id: student && student.objectId,
                    name,
                    isFlaggedByStudent,
                })
            })
        } else if (isFlaggedByStudent) {
            // Student FLAGGED the question but didn't answer it - group as "ua"
            if (!acc['ua']) {
                acc['ua'] = []
            }
            acc['ua'].push({
                id: student && student.objectId,
                name,
                isFlaggedByStudent,
            })
        }
        return acc
    }, {} as { [key: string]: {
        id: string
        name: string
        isFlaggedByStudent: boolean
    }[] })
})

const studentsTableDimensions = (choiceKey: string) => {
    const numStudents = Object.entries(studentsAnsweredByAnswerChoice.value?.[choiceKey] || []).length

    const height = numStudents === 1
        ? '18px'
        : numStudents > 1 && numStudents < 7
            ? '36px'
            : `${Math.ceil(numStudents / 3) * 18}px`

    const width = numStudents < 3
        ? '146px'
        : numStudents >= 3 && numStudents < 5
            ? '292px'
            : '438px'

    return {
        height,
        width,
    }
}

const mappedStudents = (choiceKey: string) => {
    /*
        There is a specific order in which we have to fill the rows in the table for visible names.
        Here are the rules for filling the names in:

        1. The first two names go in the first column
        2. The third and fourth names go in the second column
        3. Any subsequent names fill out the first two rows
        4. Any additional names after those fill out the last row
        5. Alphabetical by col
        6. Names truncate when they don't fit the width of the table cell
    */
    const rows: TName[] = []
    const studentsVal = studentsAnsweredByAnswerChoice.value?.[choiceKey] || []
    if (studentsVal.length) {
        const sortedStudents = studentsVal.sort((a, b) => a.name.localeCompare(b.name))
        sortedStudents.forEach(sortedStudent => {
            rows.push({
                id: sortedStudent.id,
                name: sortedStudent.name,
                isFlagged: sortedStudent.isFlaggedByStudent,
            })
        })
    }
    return rows
}

const previousPage = computed(() => {
    if (route.path.search('/stats/') > -1) {
        return 'Stats'
    }

    if (route.path.search('/content/') > -1) {
        return 'Questions'
    }

    return ''
})

const questionIndex = computed(() => {
    if (isFlaggedQuestion.value) {
        return groupFlaggedQuestions.value.findIndex(q => q.serial === currentSerial.value)
    }
    return allQuestions.value.findIndex(q => q.serial === currentSerial.value)
})

const previousFlaggedQuestion = computed(() => {
    return groupFlaggedQuestions.value[questionIndex.value - 1]
})

const previousQuestion = computed(() => {
    return allQuestions.value[questionIndex.value - 1]
})

const nextFlaggedQuestion = computed(() => {
    return groupFlaggedQuestions.value[questionIndex.value + 1]
})

const nextQuestion = computed(() => {
    return allQuestions.value[questionIndex.value + 1]
})

const isPreviousArrowDisabled = computed(() => {
    if (isFlaggedQuestion.value && !groupFlaggedQuestions.value.length) {
        return true
    }
    return questionIndex.value === 0
})

const isNextArrowDisabled = computed(() => {
    if (isFlaggedQuestion.value) {
        return questionIndex.value >= (groupFlaggedQuestions.value.length - 1)
    }
    return questionIndex.value >= (allQuestions.value.length - 1)
})

const pathName = computed(() => {
    return route.name as string
})

const quizSettings = computed(() => {
    return userModule.state.user?.quizSettings || null
})

const allowKeyboardShortcuts = computed(() => {
    return !!quizSettings.value?.enableKeyboardShortcuts
})

const quizContainerEl = computed(() => {
    const quizContainerRef = quizContainer.value
    return (quizContainerRef && '$el' in quizContainerRef) ? quizContainerRef.$el : null
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const currentSubject = computed(() => {
    const subject = currentQuestion.value?.subject as Study.Class.SubjectJSON
    return subject.name
})

const currentQuestion = computed(() => {
    if (isFlaggedQuestion.value && groupFlaggedQuestions.value[questionIndex.value]) {
        return allQuestions.value.find(q => q.serial === groupFlaggedQuestions.value[questionIndex.value]?.serial)
    }
    return allQuestions.value[questionIndex.value]
})

const globalMetricsForQuestion = computed((): Study.Class.GlobalQuestionMetricJSON | null => {
    if (!showGroupMetrics.value || !currentQuestion.value) {
        return null
    }
    return currentGroupMetrics(currentQuestion.value.serial) as unknown as Study.Class.GlobalQuestionMetricJSON
})

const isTEIsQuestion = computed(() => {
    if (!currentQuestion.value?.type) {
        return false
    }

    return [
        'Matrix Checkbox', 'Matrix Radio Button', 'Build List', 'Multi-Part Multiple Choice',
    ].includes(currentQuestion.value?.type)
})

const currentGroupExam = computed(() => {
    return examModule.getters.getCurrentGroupExam()
})

const currentGroupMetrics = (serial: string) => {
    // numStudentsAnswered
    const question = groupStats.value.questionStats.find(q => q.serial === serial)
    const numStudentsAnswered = question?.answers.length || 0

    // numStudentsCorrect
    const numStudentsCorrect = question?.answers.filter(a => a.isCorrect).length || 0

    // numStudentsFlagged
    const numStudentsFlagged = groupFlagged.value.find(fq => fq.serial === serial)?.numStudentsFlagged
        || 0

    // Percentage correct defaults to --
    let percentStudentsCorrect: number | string = '--'

    if (numStudentsAnswered) {
        percentStudentsCorrect = Math.round((numStudentsCorrect / numStudentsAnswered) * 100) || 0
    }

    // Want an obj like so:
    // { a3: 2, d1: 20 }
    const selectedChoicesByCount = question?.answers.reduce((acc, answer) => {
        answer.selectedChoices.forEach(choice => {
            acc[choice] = (acc[choice] ?? 0) + 1
        })
        return acc
    }, {} as Record<TChoiceKey, number>) || {
        // Default values for when a question is not answered
        'a1': 0,
        'b1': 0,
        'c1': 0,
        'd1': 0,
        'a2': 0,
        'b2': 0,
        'c2': 0,
        'd2': 0,
        'a3': 0,
        'b3': 0,
        'c3': 0,
        'd3': 0,
        'a4': 0,
        'b4': 0,
        'c4': 0,
        'd4': 0,
    }

    const cge = currentGroupExam.value?.examMetadata.objectId as string
    const examGuid = examMetadata.value[cge]?.examGuid ?? ''
    const appId = examMetadata.value[cge]?.appId ?? ''

    return {
        // Group Metrics
        answeredCorrectlyCount: numStudentsCorrect,
        answeredIncorrectlyCount: numStudentsAnswered - numStudentsCorrect,
        questionSerial: serial,
        examGuid,
        examType: appId,
        choiceStats: selectedChoicesByCount,
        // Tiles
        numStudentsAnswered,
        percentStudentsCorrect,
        numStudentsFlagged,
    }
}

const goBack = () => {
    emit('close')
}

const clickPrevious = () => {
    if (previousFlaggedQuestion.value && isFlaggedQuestion.value) {
        router.replace({
            name: pathName.value,
            query: {
                questionSerial: previousFlaggedQuestion.value.serial,
                type: 'flagged',
            },
        })
    }

    if (previousQuestion.value && !isFlaggedQuestion.value) {
        router.replace({
            name: pathName.value,
            query: {
                questionSerial: previousQuestion.value.serial,
                type: 'all',
            },
        })
    }
}

const clickNext = () => {
    if (nextFlaggedQuestion.value && isFlaggedQuestion.value) {
        router.replace({
            name: pathName.value,
            query: {
                questionSerial: nextFlaggedQuestion.value.serial,
                type: 'flagged',
            },
        })
    }

    if (nextQuestion.value && !isFlaggedQuestion.value) {
        router.replace({
            name: pathName.value,
            query: {
                questionSerial: nextQuestion.value.serial,
                type: 'all',
            },
        })
    }
}

const toggleGlobalMetrics = (newVal: boolean) => {
    showGroupMetrics.value = newVal
}

const listQuestionClick = (serial: string) => {
    if (serial !== currentSerial.value && isFlaggedQuestion.value) {

        if (filterSubjects.value.length) {
            router.replace({
                name: pathName.value,
                query: {
                    questionSerial: serial,
                    type: 'flagged',
                    subject: filterSubjects.value,
                },
            })
            return
        }

        router.replace({
            name: pathName.value,
            query: {
                questionSerial: serial,
                type: 'flagged',
            },
        })
    }

    if (serial !== currentSerial.value && !isFlaggedQuestion.value) {
        if (filterSubjects.value.length) {
            router.replace({
                name: pathName.value,
                query: {
                    questionSerial: serial,
                    type: 'all',
                    subject: filterSubjects.value,
                },
            })
            return
        }

        router.replace({
            name: pathName.value,
            query: {
                questionSerial: serial,
                type: 'all',
            },
        })
    }
}

const updateFilter = ({ questionType, subjects, trimmedSubjects }:
{ questionType: string; subjects: string[]; trimmedSubjects: string[] }) => {
    filterType.value = questionType
    filterSubjects.value = trimmedSubjects
    checkboxSubjects.value = subjects

    if (questionType === 'flagged') {
        const isCurrentQuestionWithinFilter = groupFlaggedQuestions.value.some(q => q.serial === currentSerial.value)
        router.replace({
            name: pathName.value,
            query: {
                questionSerial: isCurrentQuestionWithinFilter
                    ? currentSerial.value
                    : groupFlaggedQuestions.value.length ? groupFlaggedQuestions.value[0]?.serial : undefined,
                type: questionType,
                subject: trimmedSubjects,
            },
        })
    }

    if (questionType === 'all' && allQuestions.value.length) {
        const isCurrentQuestionWithinFilter = allQuestions.value.some(q => q.serial === currentSerial.value)
        router.replace({
            name: pathName.value,
            query: {
                questionSerial: isCurrentQuestionWithinFilter ? currentSerial.value : allQuestions.value[0]?.serial,
                type: questionType,
                subject: trimmedSubjects,
            },
        })
    }
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    userModule.actions.updateQuizSettings({
        enableKeyboardShortcuts: newVal,
    })
}

/* istanbul ignore next */
const listViewMouseEnter = () => {
    listViewHover.value = true
    showListViewTooltip.value = true
}

/* istanbul ignore next */
const listViewMouseLeave = () => {
    listViewHover.value = false
    showListViewTooltip.value = false
}

/* istanbul ignore next */
const fullViewMouseEnter = () => {
    fullViewHover.value = true
    showFullViewTooltip.value = true
}

/* istanbul ignore next */
const fullViewMouseLeave = () => {
    fullViewHover.value = false
    showFullViewTooltip.value = false
}

onMounted(async () => {
    const routeGroupId = route.params.groupId
    const routeStudentId = route.params.studentId
    await Promise.all([
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        studentModule.actions.updateCurrentStudentId(routeStudentId as string),
        statsModule.actions.fetchCurrentGroupAggStats(),
    ])

    if (route.query.subjects as string[]) {
        filterSubjects.value = route.query.subjects as string[]
    }
    isLoading.value = false
})

watch([ () => route.params.groupId, () => route.params.studentId ], ([ newGroupId, newStudentId ]) => {
    if (newGroupId) {
        groupModule.actions.updateCurrentGroupId(newGroupId as string)
        if (newStudentId) {
            studentModule.actions.updateCurrentStudentId(newStudentId as string)
        }
    }
})

watch(() => route.query.type, (type) => {
    if (type === 'flagged') {
        filterType.value = 'flagged'
    }
})

watch(showNamesChecked, (val) => {
    emit('toggleShowNames', val)
})
</script>

<template>
    <div class="group-question-review">
        <QuizContainer
            v-if="groupStats && currentGroup"
            ref="quizContainer"
            class="group-question-review"
            :show-side="showSideBar"
        >
            <template #header>
                <PocketButton
                    class="group-question-review__back-btn"
                    type="icon-yellow"
                    @click="goBack"
                >
                    <Icon class="group-question-review__back-btn-icon" type="arrow" />
                    Back to {{ previousPage }}
                </PocketButton>
                <div class="group-question-review__frame-context">
                    {{ currentGroup }}: {{ isFlaggedQuestion ?
                        'Flagged Questions' : currentExamName + ' Question Bank' }}
                </div>
                <QuestionReviewPagination
                    class="group-question-review__pagination"
                    :total="isFlaggedQuestion ? groupFlaggedQuestions.length : allQuestions.length"
                    :current-page="questionIndex"
                    @changePage="changePage($event)"
                />
            </template>
            <template #side>
                <div v-if="showSideBar" class="group-question-review__side-bar">
                    <div class="group-question-review__side-bar-header">
                        Questions
                        <PocketButton
                            type="icon-yellow"
                            :icon="(filterSubjects.length) ? 'filterActive' : 'filter'"
                            :aria-label="`${
                                (filterSubjects.length) ? 'Filters selected, ' : ''
                            }Show filters`"
                            @click="showFilterDropdown = !showFilterDropdown"
                            @keypress.enter="showFilterDropdown = !showFilterDropdown"
                            @mousedown.prevent
                        />
                        <FilterDropdown
                            v-if="showFilterDropdown"
                            class="group-question-review__filter-dropdown"
                            :default-question-type="filterType"
                            :default-subjects="filterSubjects"
                            :student-or-group="'group'"
                            aria-live="polite"
                            @filterChange="updateFilter"
                            @close="showFilterDropdown = false"
                        />
                    </div>
                    <div
                        v-if="!isFlaggedQuestion && allQuestions.length"
                        class="group-question-review__list"
                    >
                        <div
                            v-for="(q, index) in allQuestions"
                            :key="index"
                            tabindex="0"
                            class="group-question-review__list-prompt"
                            :class="{
                                'group-question-review__list-prompt--active': index === questionIndex,
                            }"
                            @click="listQuestionClick(q.serial)"
                            @keydown.enter.stop.prevent="listQuestionClick(q.serial)"
                            @mousedown.prevent
                            v-html="formattedQuestionPrompt(q)"
                        />
                    </div>
                    <div
                        v-if="isFlaggedQuestion && groupFlaggedQuestions.length"
                        class="group-question-review__list"
                    >
                        <div
                            v-for="(q, index) in groupFlaggedQuestions"
                            :key="index"
                            tabindex="0"
                            class="group-question-review__list-prompt"
                            :class="{
                                'group-question-review__list-prompt--active': index === questionIndex,
                            }"
                            @click="listQuestionClick(q.serial)"
                            @keydown.enter.stop.prevent="listQuestionClick(q.serial)"
                            @mousedown.prevent
                            v-html="formattedGroupFlaggedQuestionPrompt(q)"
                        />
                    </div>
                    <div
                        v-if="isFlaggedQuestion && !groupFlaggedQuestions.length"
                        class="group-question-review__list-empty"
                    >
                        Looks like there aren’t any questions to match your filter criteria.
                    </div>
                </div>
            </template>
            <template #question>
                <Icon
                    v-if="isLoading"
                    class="group-question-review__loading"
                    type="loading2"
                />
                <Question
                    v-if="!isLoading && currentQuestion"
                    :key="`${currentQuestion.objectId}-${allowKeyboardShortcuts}`"
                    class="group-question-review__question"
                    :container-el="quizContainerEl"
                    :question="currentQuestion"
                    :question-number="questionIndex + 1"
                    :quiz-length="isFlaggedQuestion ? groupFlaggedQuestions.length : allQuestions.length"
                    :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                    :image-url-prefix="`${s3Url || ''}/${currentQuestion.compositeKey.toUpperCase()}/`"
                    :review-mode="true"
                    :is-teach-group-review="true"
                    :default-show-explanation="true"
                    :auto-focus-prompt="true"
                    :hide-references="currentExamMetadata?.hideReferences"
                    :global-metrics="globalMetricsForQuestion"
                    :show-names="showNamesChecked && showGroupMetrics ? studentsAnsweredByAnswerChoice : null"
                    @close="goBack"
                    @previousQuestion="clickPrevious"
                    @nextQuestion="clickNext"
                >
                    <template #context>
                        <div class="group-question-review__subject">
                            {{ currentSubject }}
                        </div>
                    </template>
                    <template #metricsTiles>
                        <div
                            v-if="showGroupMetrics"
                            class="group-question-review__stats-summary"
                        >
                            <div class="group-question-review__stats-summary-total">
                                <div class="group-question-review__stats-summary-value">
                                    {{ currentGroupMetrics(currentQuestion.serial).numStudentsAnswered }}
                                </div>
                                <div class="group-question-review__stats-summary-label">
                                    Students Answered
                                </div>
                            </div>
                            <div class="group-question-review__stats-summary-correct">
                                <div class="group-question-review__stats-summary-value">
                                    {{ currentGroupMetrics(currentQuestion.serial).percentStudentsCorrect }}%
                                </div>
                                <div class="group-question-review__stats-summary-label">
                                    Answered Correctly
                                </div>
                            </div>
                            <div class="group-question-review__stats-summary-flagged">
                                <div class="group-question-review__stats-summary-value">
                                    {{ currentGroupMetrics(currentQuestion.serial).numStudentsFlagged }}
                                </div>
                                <div class="group-question-review__stats-summary-label">
                                    Students Flagged
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #showNamesTable="{ choiceKey }">
                        <div
                            class="group-question-review__table-container"
                        >
                            <div
                                v-if="showNamesChecked &&
                                    showGroupMetrics &&
                                    studentsAnsweredByAnswerChoice &&
                                    Object.entries(studentsAnsweredByAnswerChoice).length > 0"
                                :id="`group-question-review__view-names-table--${choiceKey}`"
                                class="group-question-review__view-names-table"
                                :style="{
                                    'height': studentsTableDimensions(choiceKey).height,
                                    'maxWidth': studentsTableDimensions(choiceKey).width,
                                }"
                            >
                                <div
                                    v-for="(student) in mappedStudents(choiceKey)"
                                    :key="`group-question-review__view-names-entry--${student.id}`"
                                    class="group-question-review__view-names-entry"
                                >
                                    <Icon
                                        v-if="student.isFlagged"
                                        type="flagContent"
                                        class="group-question-review__flag-icon"
                                    />
                                    <div
                                        v-else
                                        class="group-question-review__icon-space"
                                    />
                                    <div
                                        class="group-question-review__view-names-name"
                                    >
                                        <OverflowTooltip>
                                            {{ student.name }}
                                        </OverflowTooltip>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #unansweredFlaggedNamesTable>
                        <div
                            class="group-question-review__table-container"
                        >
                            <div
                                v-if="showNamesChecked &&
                                    showGroupMetrics &&
                                    studentsAnsweredByAnswerChoice['ua'] &&
                                    studentsAnsweredByAnswerChoice['ua'].length > 0"
                                class="group-question-review__view-names-unanswered"
                            >
                                <span
                                    class="group-question-review__view-names-title-unanswered"
                                >
                                    Unanswered, Flagged
                                </span>
                                <div
                                    class="group-question-review__view-names-container-unanswered"
                                    :style="{
                                        'height': studentsTableDimensions('ua').height,
                                        'width': studentsTableDimensions('ua').width,
                                    }"
                                >
                                    <div
                                        v-for="(student) in mappedStudents('ua')"
                                        :key="`group-question-review__view-names-entry--${student.id}`"
                                        class="group-question-review__view-names-entry"
                                    >
                                        <Icon
                                            v-if="student.isFlagged"
                                            type="flagContent"
                                            class="group-question-review__flag-icon"
                                        />
                                        <div
                                            v-else
                                            class="group-question-review__icon-space"
                                        />
                                        <OverflowTooltip
                                            class="group-question-review__view-names-name"
                                        >
                                            {{ student.name }}
                                        </OverflowTooltip>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Question>
                <div v-else class="group-question-review__no-question">
                    <div class="group-question-review__no-question-title">
                        No Results.
                    </div>
                    <div class="group-question-review__no-question-subtext">
                        No question is selected.
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="group-question-review__view-toggles">
                    <div
                        class="group-question-review__side-toggle"
                    >
                        <div
                            class="group-question-review__view-side"
                            :class="{
                                'group-question-review__view-side--active': showSideBar,
                                'group-question-review__view-side--hover': listViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show List View"
                            :aria-checked="!showSideBar"
                            @mouseenter="listViewMouseEnter"
                            @mouseleave="listViewMouseLeave"
                            @mousedown.prevent
                            @focus="showListViewTooltip = true"
                            @blur="showListViewTooltip = false"
                            @click="showSideBar = true"
                            @keydown.enter.stop.prevent="showSideBar = true"
                        >
                            <Tooltip
                                v-if="showListViewTooltip"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider || '',
                                    'color': brandColors.brandBlack || '',
                                }"
                            >
                                List View
                            </Tooltip>
                            <Icon
                                v-if="showSideBar || listViewHover"
                                class="group-question-review__view-side-active-icon"
                                type="sideBarActive"
                            />
                            <Icon
                                v-else
                                class="group-question-review__view-side-icon"
                                type="sideBar"
                            />
                        </div>
                        <div
                            class="group-question-review__view-full"
                            :class="{
                                'group-question-review__view-full--active': !showSideBar,
                                'group-question-review__view-full--hover': fullViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show Full View"
                            :aria-checked="!showSideBar"
                            @mouseenter="fullViewMouseEnter"
                            @mouseleave="fullViewMouseLeave"
                            @mousedown.prevent
                            @focus="showFullViewTooltip = true"
                            @blur="showFullViewTooltip = false"
                            @click="showSideBar = false"
                            @keydown.enter.stop.prevent="showSideBar = false"
                        >
                            <Tooltip
                                v-if="showFullViewTooltip"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider || '',
                                    'color': brandColors.brandBlack || '',
                                }"
                            >
                                Full View
                            </Tooltip>
                            <Icon
                                v-if="!showSideBar || fullViewHover"
                                class="group-question-review__view-full-active-icon"
                                type="fullViewActive"
                            />
                            <Icon
                                v-else
                                class="group-question-review__view-full-icon"
                                type="fullView"
                            />
                        </div>
                    </div>
                    <GlobalMetricsToggle
                        class="group-question-review__global-metrics-toggle-left"
                        :show-global-metrics="showGroupMetrics"
                        @toggleGlobalMetrics="toggleGlobalMetrics"
                    >
                        <template #metricsTooltipText>
                            Group Stats: {{ currentGroupName }}
                        </template>
                    </GlobalMetricsToggle>
                    <div
                        v-if="showGroupMetrics && students.length < 40"
                        class="group-question-review__view-names-container"
                    >
                        <Checkbox
                            v-model="showNamesChecked"
                            class="group-question-review__view-names-checkbox"
                            :is-dark-mode="true"
                            :disabled="isTEIsQuestion"
                        />
                        <span
                            class="group-question-review__view-names"
                            :class="{
                                'group-question-review__view-names--disabled-teis-question': isTEIsQuestion,
                            }"
                        >
                            View Names
                        </span>
                    </div>
                </div>
                <div
                    class="group-question-review__controls"
                >
                    <Icon
                        class="group-question-review__previous-arrow"
                        :class="{
                            'group-question-review__previous-arrow--disabled': isPreviousArrowDisabled,
                        }"
                        :tabindex="isPreviousArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        aria-label="Previous question"
                        :aria-disabled="isPreviousArrowDisabled"
                        @click="clickPrevious"
                        @keydown.enter.stop.prevent="clickPrevious"
                        @mousedown.prevent
                    />
                    <Icon
                        tabindex="0"
                        class="group-question-review__next-arrow"
                        :class="{
                            'group-question-review__next-arrow--disabled': isNextArrowDisabled,
                        }"
                        type="paginationArrow"
                        aria-label="Next question"
                        :aria-disabled="isNextArrowDisabled"
                        @click="clickNext"
                        @keydown.enter.stop.prevent="clickNext"
                        @mousedown.prevent
                    />
                </div>
                <KeyboardShortcutsButton
                    class="group-question-review__keyboard-shortcuts-btn"
                >
                    <template
                        #keyboardShortcutsModal="{
                            showKeyboardShortcutsModal,
                            closeModalFunc
                        }"
                    >
                        <Portal to="modal">
                            <KeyboardShortcutsModal
                                v-if="showKeyboardShortcutsModal"
                                key="questionReviewKeyboardShortcutsModal"
                                keyboard-mode="teach"
                                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                aria-live="polite"
                                @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                @close="closeModalFunc"
                            />
                        </Portal>
                    </template>
                </KeyboardShortcutsButton>
            </template>
        </QuizContainer>
    </div>
</template>

<style lang="scss" scoped>
.group-question-review {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1002;
    box-sizing: border-box;

    &__back-btn-icon {
        width: 14px;
        height: 12px;
        transform: rotate(180deg);
        margin-right: 8px;
    }

    &__frame-context {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        color: $white;
        font-size: 15px;
        letter-spacing: -0.09px;
        line-height: 18px;
    }

    &__subject {
        width: 100%;
        max-width: 492px;
        color: $slate-01;
        margin-top: 48px;
        margin-bottom: 8px;
        padding: 0 16px;
        box-sizing: border-box;
        font-size: 14px;
    }

    &__pagination {
        display: flex;
        align-items: center;
    }

    &__page-nums {
        margin: 0 7px;
    }

    &__side-bar {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__filter-dropdown {
        position: absolute;
        left: 0;
        top: 44px;
        z-index: 2;
    }

    &__active-indicator {
        position: absolute;
        right: -10px;
        transform: translateY(-50%);
        width: 10px;
        height: 18px;
        background-color: $ash;
        border: 5px solid white;
    }

    &__side-bar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 44px;
        padding: 0 6px 0 12px;
        font-weight: 600;
        font-size: 16px;
        line-height: 19px;
    }

    &__list {
        position: relative;
        overflow-y: auto;
        flex: 1;
    }

    &__list-empty {
        color: $white;
        font-size: 13px;
        max-width: 160px;
        margin: 58px auto 0;
        line-height: 18px;
        opacity: 0.82;
        text-align: center;
    }

    &__list-prompt {
        position: relative;
        box-sizing: border-box;
        color: rgba($white, 0.75);
        padding: 14px 11px 14px 12px;
        font-size: 14px;
        letter-spacing: -0.09px;
        line-height: 17px;
        cursor: pointer;
        border-top: 2px solid $jet;
        outline: none;

        &:last-child {
            border-bottom: 2px solid $mariner;
        }

        &--active {
            color: $white;
            background-color: $ash;
            border-radius: 6px;
            border-color: $charcoal;

            &:last-child {
                border-bottom: 0;
            }
        }

        &--active + & {
            border-top-color: $charcoal;
        }

        &:hover {
            color: $white;
        }

        &:focus {
            color: $white;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                border-radius: 6px;
                border: 1px solid $butterscotch;
            }
        }
    }

    &__loading {
        position: absolute;
        left: 50%;
        top: 25%;
        transform: translateX(-50%) scale(1.5);
    }

    &__question {
        position: relative;
        height: 100%;
    }

    &__stats-summary {
        margin-top: 24px;
        display: flex;
        width: 100%;
        max-width: 492px;
        padding-bottom: 50px;
    }

    &__stats-summary-total {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        margin-right: 12px;
        padding: 9px 0;
        border-radius: 6px;
        border: 1px solid rgba($pewter, 0.85);
        background-color: $white;
    }

    &__stats-summary-correct {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        margin-right: 12px;
        padding: 9px 0;
        border-radius: 6px;
        border: 1px solid rgba($pewter, 0.85);
        background-color: $white;
    }

    &__stats-summary-flagged {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        padding: 9px 0;
        border-radius: 6px;
        border: 1px solid rgba($pewter, 0.85);
        background-color: $white;
    }

    &__stats-summary-value {
        font-weight: 600;
        font-size: 26px;
        letter-spacing: 0.26px;
        line-height: 31px;
    }

    &__stats-summary-label {
        color: $ash;
        font-size: 13px;
        line-height: 16px;
        text-align: center;

        &--dark {
            color: $white;
        }
    }

    &__no-question {
        max-width: 242px;
        margin: 158px auto 0;
        text-align: center;
    }

    &__no-question-title {
        font-size: 16px;
        line-height: 19px;
        margin-bottom: 9px;
        font-weight: 600;
    }

    &__no-question-subtext {
        font-size: 14px;
        line-height: 17px;
        max-width: 242px;
        color: $ash;
    }

    &__view-names-container,
    &__view-toggles {
        display: flex;
        align-items: center;
    }

    &__view-names-flexbox {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
    }

    &__table-container {
        width: 100%;
    }

    &__view-names-table {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        flex-wrap: wrap;
        overflow: hidden;
        background-color: $gray-background;
        padding: 6px 0 30px 14px;
    }

    &__view-names-entry {
        flex: 1 0 10%;
        display: flex;
        width: 146px;
        max-height: 18px;
        margin-right: 24px;
    }

    &__view-names-unanswered {
        padding: 15px 0 15px 14px;
    }

    &__view-names-container-unanswered {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        overflow: hidden;
        background-color: $gray-background;
        padding-top: 15px;
    }

    &__view-names-title-unanswered {
        font-size: 16.5px;
    }

    &__view-names-name {
        color: $ash;
        font-size: 13px;
        line-height: 18px;
        max-width: 129px;
    }

    &__icon-space {
        min-width: 17px;
        height: 0;
    }

    &__flag-icon {
        padding: 2px 0 0 0;
        margin: 0 5px 0 1px;
        color: $steel;
    }

    &__side-toggle {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 80px;
        height: 37px;
        margin-right: 25px;
        border-radius: 6px;

        &::after {
            content: '';
            position: absolute;
            height: 100%;
            right: -13px;
            border-right: 1px solid $slate-03;
        }
    }

    &__view-side,
    &__view-full {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 37px;
        border-radius: 6px;
        outline: none;

        &--hover {
            background-color: rgba($white, 0.1);
        }

        &--active {
            background-color: $charcoal;
            z-index: 2;
        }

        &:focus::before {
            content: '';
            position: absolute;
            border: 1px solid $butterscotch;
            width: 40px;
            height: 36px;
            top: 0;
            left: 0;
            border-radius: 4px;
            box-sizing: border-box;
        }

        &:not(&--active) {
            cursor: pointer;
        }

        .uikit-tooltip {
            position: absolute;
            left: calc(50%);
            top: calc(-100%);
        }
    }

    &__view-side {
        left: 0;

        &--hover {
            &:not(&--active) {
                padding-right: 6px;
                border-radius: 6px 0 0 6px;
            }
        }
    }

    &__view-full {
        right: 0;

        &--hover {
            &:not(&--active) {
                padding-left: 6px;
                border-radius: 0 6px 6px 0;

                &:focus::before {
                    left: 6px;
                }
            }
        }
    }

    &__footer {
        display: flex;
        justify-content: space-between;
    }

    &__controls {
        display: flex;
        align-items: center;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);

        .uikit-tooltip {
            position: absolute;
            left: 50%;
            top: -70%;
        }
    }

    &__flag-count-row {
        display: flex;
        justify-content: center;
        background-color: $jet;
        border-radius: 20px;
        width: 71px;
        height: 32px;
    }

    &__global-metrics-toggle-left {
        float: left;
    }

    &__view-names-checkbox {
        margin-left: 25px;
    }

    &__view-names {
        font-size: 14px;
        line-height: 19px;
        padding-left: 10px;

        &--disabled-teis-question {
            color: $pewter;
        }
    }

    &__next-arrow {
        padding-left: 275px;
    }

    &__previous-arrow,
    &__next-arrow,
    &__top-previous-arrow,
    &__top-next-arrow {
        &--disabled {
            opacity: 0.35;
        }

        &:not(&--disabled) {
            cursor: pointer;

            @media (hover: hover) {
                &:hover {
                    color: $banana-bread;
                }
            }
        }

        &:focus {
            outline: none;
            color: $banana-bread;
        }
    }

    &__top-previous-arrow,
    &__top-next-arrow {
        width: 24px;
        height: 24px;
    }

    &__previous-arrow,
    &__top-previous-arrow {
        transform: rotate(180deg);
    }

    &__flag-count {
        font-size: 16px;
        line-height: 19px;
        margin-top: 6px;
        margin-bottom: 5px;
        margin-right: -1px;
    }

    &__flag {
        margin-left: 7px;
        margin-top: 8px;
        height: 14px;
        width: 10px;

        &--flagged-by-students {
            color: $white;
        }
    }

    &__keyboard-shortcuts-btn {
        position: absolute;
        right: 0;
    }

    :deep(.uikit-question__close-icon) {
        display: none;
    }

    :deep(.uikit-checkbox__check) {
        margin: 0;
    }
}
</style>
